import { digger, patcher } from '@cbre/digger';
import { Column } from '@miq/catalog';
import { Translator } from '@miq/utils';
import { RecordModel } from '@records/clipboard/clipboard-list/clipboard-list.component';
import { DataObject } from '@shared/models/data-object.model';

// TODO: remove bellow refs & replace with @shared/utils/helpers/catalog.helpers.ts so we don't have to export them
export {
  ColumnSimpleKeys,
  findColumnInSectionsBy as findColumnBy,
  findColumnInSectionsByField as findColumnByField,
  updateColumnByField,
} from './catalog.helpers';

export const isARecordOutdated = (recordsList: RecordModel[]): boolean =>
  recordsList.some(({ isOutdated }: RecordModel) => isOutdated);

export const updateSingleClibpoardReportItemRecord = (
  dataStore: any,
  updatedRecord: RecordModel,
  field: string,
  value: any
) => {
  const currentRecordValue = dataStore[updatedRecord.recordId];
  const path = field.replaceAll('/', '.0.');
  let nextData = { ...updatedRecord, data: patcher({}, path, value) };
  if (!!currentRecordValue) {
    nextData = { ...currentRecordValue, data: patcher(currentRecordValue.data, path, value) };
  }
  dataStore[updatedRecord.recordId] = nextData;
  return dataStore;
};

/**
 * Review if value is not null or an empty string.
 *
 * @param val Value to review.
 */
export const simpleValueValidation: (val) => boolean = val => val != null && `${val}`.trim() !== '';

export const isDash: (val: string) => boolean = val => val.trim() === '-';

export const multipleSimpleValueValidation: (...values: Array<string | number>) => boolean = (...values: string[]) =>
  values.reduce((acc, val) => acc && simpleValueValidation(val), true);

/**
 * Validates UOM fields
 * @param field {Column}
 * @param uom {Column}
 */
export const validateUomFields = (field: Column, uom: Column) => {
  /**
   * https://cbremb.atlassian.net/browse/MIQ-10229
   * Some values are coming as zero without UOM form Backend.
   * Considering zero as null when validating the combination for the UOM field.
   */
  /**
   * https://cbremb.atlassian.net/browse/MIQ-10457
   * Taking zero into consideration when field value is zero and UOM field is null.
   */
  const valid =
    (field.value != null && uom.value != null) ||
    (field.value != null && field.value === 0 && uom.value == null) ||
    (field.value == null && uom.value == null);
  field.UOMValidation = field.value != null && field.value !== 0 && uom.value == null;
  uom.UOMValidation = field.value == null && uom.value != null;
  return valid;
};

/**
 * TODO: remove this fn
 * @deprecated will be removed
 * @param flatColumns
 */
export const isValuesAndUomsAreSetUp = flatColumns => {
  let isUomValuesSetUp = true;
  // loop through each column in the detail page to check for it's assoicated field and uom
  // eslint-disable-next-line complexity
  flatColumns.forEach(element => {
    if (element.field && element.field.includes('_uom_desc')) {
      // if field is a uom
      let fieldMappedToUOM; // field associated to uom
      let field = getUOM(element.field);
      if (field && field.length > 1 && field[1] === 'actual_sales_price_yearly') {
        field = ['actual_sales_price_yearly', 'actual_sales_price'];
      }
      if (field && field.length > 1) {
        fieldMappedToUOM = flatColumns.filter(col => col.field === field[1]);
      }
      if (
        (element.value != null && fieldMappedToUOM && fieldMappedToUOM[0].value != null) ||
        (element.value == null && fieldMappedToUOM && fieldMappedToUOM[0].value == null)
      ) {
        // if both value exists or doesn't exist validation should be false
        fieldMappedToUOM[0].UOMValidation = false;
        element.UOMValidation = false;
      } else {
        if (element.value != null) {
          fieldMappedToUOM[0].UOMValidation = true;
        } else {
          element.UOMValidation = true;
        }
        isUomValuesSetUp = false;
      }
    } else if (element.field && element.unit) {
      // else if element has unit check for values and add uom validation flag
      if (
        (element.value != null && element.unit.value != null) ||
        (element.value == null && element.unit.value == null)
      ) {
        element.unit.UOMValidation = false;
        element.UOMValidation = false;
      } else {
        if (element.value != null) {
          element.unit.UOMValidation = true;
        } else {
          element.UOMValidation = true;
        }
        isUomValuesSetUp = false;
      }
    }
  });
  return isUomValuesSetUp;
};

const getUOM = (field: string) => {
  return field.includes('ref')
    ? field.match(new RegExp('ref_' + '(.*)' + '_uom'))
    : field.match(new RegExp('(.*)' + '_uom'));
};

/**
 * Recursively flat an object getting all the inner objects values to the root of an
 * empty object. It also removes all the embedded methods.
 *
 * NOTE: This does not deal in any way with arrays (ie. property.usages)
 *       nor duplicated keys.
 *
 * @param obj
 */
export function flatObject(obj: DataObject): DataObject {
  return Object.keys(obj).reduce((acc, key) => {
    let newProperty = {};
    if (typeof obj[key] === 'object' && obj[key] != null) {
      newProperty = flatObject(obj[key]);
    } else if (typeof obj[key] !== 'function') {
      newProperty = { [key]: obj[key] };
    }
    return { ...acc, ...newProperty };
  }, {});
}

/**
 * Given a Entity object, it return the entity id value of it
 * @param x
 */
export const getEntityId = (x: DataObject) => digger(x, 'entity.id');

export const handleDownloadError = (error: string | any, entity: string, translationService: Translator) => {
  const errorStr = typeof error === 'string' ? error : (error?.message ?? '');
  if (errorStr.indexOf('Export/Download limit') > 0) {
    const extractedData = errorStr.split(/[^0-9]/).filter(data => data !== '');
    const userLimit = extractedData[3];
    const exceedRecords = extractedData[5];
    const downloadError =
      translationService.translate('recordsSearch.exportDownloadLimitOf') +
      ' ' +
      `${userLimit}` +
      ' ' +
      translationService.translate('shared.entityPlurals.' + entity) +
      translationService.translate('recordsSearch.per24HourPeriod') +
      translationService.translate('recordsSearch.lastestRequestRecord') +
      `${exceedRecords}` +
      translationService.translate('recordsSearch.overYourLimitAndAdjust');
    return { isDownloadLimitError: true, message: downloadError };
  } else {
    return { isDownloadLimitError: false, message: 'recordsSearch.errWhileExportingRecord' };
  }
};
